import api from './api';
import {
  MqttDevice,
  MqttMeterImageList,
  MqttDeviceConfigRequest,
  MqttMessageLogEntry,
  MqttStatus,
} from '../types';

const mqttService = {
  getStatus: async (): Promise<MqttStatus> => {
    const response = await api.get('/mqtt/status');
    return response.data;
  },

  getDevices: async (): Promise<MqttDevice[]> => {
    const response = await api.get('/mqtt/devices');
    return response.data;
  },

  renameDevice: async (deviceId: number, name: string | null): Promise<void> => {
    await api.put(`/mqtt/devices/${deviceId}/name`, { name });
  },

  deleteDevice: async (deviceId: number): Promise<void> => {
    await api.delete(`/mqtt/devices/${deviceId}`);
  },

  mapChannel: async (channelId: number, meterId: number | null): Promise<void> => {
    await api.put(`/mqtt/channels/${channelId}/map`, { meterId });
  },

  /** Einspeise-Zählwerk des Kanals auf einen PV-Zähler buchen; null hebt es auf. */
  setFeedInMeter: async (channelId: number, meterId: number | null): Promise<void> => {
    await api.put(`/mqtt/channels/${channelId}/feed-in-meter`, { meterId });
  },

  getMessages: async (params?: {
    deviceId?: string;
    status?: string;
    limit?: number;
  }): Promise<MqttMessageLogEntry[]> => {
    const response = await api.get('/mqtt/messages', { params });
    return response.data;
  },

  publishConfig: async (
    deviceId: number,
    config: MqttDeviceConfigRequest
  ): Promise<void> => {
    await api.post(`/mqtt/devices/${deviceId}/config`, config);
  },

  // --- Zählerfotos ---

  /**
   * Fordert bei der lesenden Platine ein Foto an.
   *
   * Antwortet, sobald das Kommando auf dem Broker liegt — NICHT, wenn das Bild
   * da ist. Das Bild kommt asynchron über MQTT; danach die Liste pollen.
   */
  requestMeterPhoto: async (portalMeterId: number): Promise<{ reader: string }> => {
    const response = await api.post(`/mqtt/portal-meters/${portalMeterId}/request-image`);
    return response.data;
  },

  getMeterPhotos: async (portalMeterId: number): Promise<MqttMeterImageList> => {
    const response = await api.get(`/mqtt/portal-meters/${portalMeterId}/images`);
    return response.data;
  },

  /**
   * Lädt die Bytes eines Fotos als Object-URL.
   *
   * Umweg über einen Blob, weil die API einen Bearer-Token im Header erwartet
   * und ein `<img src>` keinen mitschickt. Der Aufrufer muss die URL wieder
   * freigeben (URL.revokeObjectURL), sonst bleibt das Bild im Speicher.
   */
  getMeterPhotoUrl: async (mqttMeterRowId: number, imageId: number): Promise<string> => {
    const response = await api.get(`/mqtt/meters/${mqttMeterRowId}/images/${imageId}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  reconnect: async (): Promise<{ connected: boolean }> => {
    const response = await api.post('/mqtt/reconnect');
    return response.data;
  },
};

export default mqttService;
